import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { ROUTES } from "../../routes/routes.constants";
import { getPostLoginPath } from "../../utils/postLoginPath";
import { loginOtpPending, loginSuccess } from "../../redux/authSlice";
import EmailInput from "../../common_components/EmailInput";
import PasswordInput from "../../common_components/PasswordInput";
import "../../App.css";
import ValidationMessage from "../../common_components/Validation";
import { emailRegex } from "../../helper/regex";
import { ApiCall } from "../../helper/axios";
import CustomLoader from '../../common_components/CustomLoader';
import AuthErrorBanner from "./AuthErrorBanner";
import AppButton from "../../common_components/AppButton";
import { Dialog, Stack } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// Validation schema using Yup
const validationSchema = Yup.object({
    email: Yup.string()
        .trim()
        .required(ValidationMessage.emailRequired)
        .matches(emailRegex, ValidationMessage.emailInvalid)
        .email(ValidationMessage.emailInvalid),
    password: Yup.string()
        .trim()
        .required(ValidationMessage.passwordRequired)
});
export default function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [apiError, setApiError] = useState(null);
    const [successBanner, setSuccessBanner] = useState(null);
    const [selectionData, setSelectionData] = useState(null);
    const fromPathRef = React.useRef(location.state?.from || ROUTES.home.path);
    useEffect(() => {
        if (location?.state) {
            if (location.state?.from) {
                fromPathRef.current = location.state.from;
            }
            setSuccessBanner(location?.state);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);
    const searchParams = new URLSearchParams(location.search);
    const initialEmail = searchParams.get('email') || "";
    const initialPassword = searchParams.get('password') || "";
    const fromPath = fromPathRef.current;
    const formik = useFormik({
        initialValues: {
            email: initialEmail,
            password: initialPassword,
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                const res = await ApiCall('POST', "/api/auth/login", values);
                if (res?.data?.success && res?.status === 200) {
                    const result = res?.data?.data;
                    setApiError(null);
                    if (result?.requires_selection) {
                        setSelectionData(result);
                        formik.setSubmitting(false);
                    }
                    else if (result?.requires_otp) {
                        dispatch(loginOtpPending({
                            token: result?.temp_token,
                            otpEntityType: result?.entity_type,
                            otpUserId: result?.user_id,
                        }));
                        navigate(ROUTES.verify.path, { replace: true });
                    }
                    else {
                        dispatch(loginSuccess({
                            user: result?.user,
                            token: result?.token,
                            permissions: result?.permissions,
                            entity_type: result?.entity_type,
                            requiresOtp: false,
                        }));
                        let finalPath = fromPath;
                        if (fromPath === ROUTES.home.path || fromPath === ROUTES.login.path || fromPath === ROUTES.root.path) {
                            finalPath = getPostLoginPath(result);
                        }
                        navigate(finalPath, { replace: true });
                    }
                }
                else {
                    const msg = res?.response?.data?.message || 'Something went wrong';
                    setApiError({ title: msg, description: "Please try again." });
                    formik.setSubmitting(false);
                }
            }
            catch (error) {
                setApiError({ title: 'Login failed. Please check your email and password and try again.', description: "" });
                formik.setSubmitting(false);
            }
        },
    });
    useEffect(() => {
        if (initialEmail || initialPassword) {
            formik.setValues({
                email: initialEmail,
                password: initialPassword
            });
        }
    }, [initialEmail, initialPassword]);
    const handleChange = (name, e) => {
        const value = e?.target?.value || '';
        if (apiError)
            setApiError(null);
        if (successBanner)
            setSuccessBanner(null);
        formik.setFieldValue(name, value);
    };
    const handleBlur = (name) => {
        formik.setFieldTouched(name, true);
    };
    const handleSelectLogin = async (as) => {
        try {
            setSelectionData(null);
            formik.setSubmitting(true);
            const values = { ...formik.values, login_as: as };
            const res = await ApiCall('POST', "/api/auth/login", values);
            if (res?.data?.success && res?.status === 200) {
                const result = res?.data?.data;
                if (result?.requires_otp) {
                    dispatch(loginOtpPending({
                        token: result?.temp_token,
                        otpEntityType: result?.entity_type,
                        otpUserId: result?.user_id,
                    }));
                    navigate(ROUTES.verify.path, { replace: true });
                }
                else {
                    dispatch(loginSuccess({
                        user: result?.user,
                        token: result?.token,
                        permissions: result?.permissions,
                        entity_type: result?.entity_type,
                        requiresOtp: false,
                    }));
                    let finalPath = fromPath;
                    if (fromPath === ROUTES.home.path || fromPath === ROUTES.login.path || fromPath === ROUTES.root.path) {
                        finalPath = getPostLoginPath(result);
                    }
                    navigate(finalPath, { replace: true });
                }
            }
            else {
                const msg = res?.response?.data?.message || 'Something went wrong';
                setApiError({ title: msg, description: "Please try again." });
            }
        }
        catch (error) {
            setApiError({ title: 'Login failed. Please check your email and password and try again.', description: "" });
        }
        finally {
            formik.setSubmitting(false);
        }
    };
    return (_jsxs(Box, { className: "login-page-container", children: [_jsx(Box, { className: "login-logo-container", sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: _jsx("img", { alt: "GROUP 8A Logo", src: "/assets/loginImage/logo-final.svg", className: "login-logo-img" }) }), _jsx(Paper, { elevation: 0, className: "login-form-card", children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { component: "form", onSubmit: formik.handleSubmit, className: "login-form", children: [_jsxs(Box, { className: "login-header", children: [_jsx(Typography, { variant: "h4", className: "login-title", children: "WELCOME TO G8A INSIGHTS" }), _jsx(Typography, { variant: "body2", className: "login-subtitle", children: "Performance starts here. Access the data and insights powering smarter brand growth." })] }), _jsxs(Box, { className: "login-form-fields", children: [_jsxs(Box, { className: "login-form-inputs-wrapper", children: [_jsxs(Box, { className: `login-form-inputs ${successBanner ? 'success-banner' : ''}`, children: [(apiError || successBanner) && (_jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(AuthErrorBanner, { message: (apiError || successBanner), flag: Boolean(successBanner) }) })), _jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(EmailInput, { label: "Email", placeholder: "Enter your email", type: "email", value: formik.values.email, onChange: (e) => handleChange('email', e), onBlur: (e) => handleBlur('email'), autoComplete: "off", errors: formik.errors.email, touched: formik.touched.email, maxLength: 50 }) }), _jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(PasswordInput, { label: "Password", Placeholder: 'Enter Password', value: formik.values.password, errors: formik.errors.password, touched: formik.touched.password, name: "password", onChange: (e) => handleChange('password', e), onBlur: (e) => handleBlur('password'), type: 'password' }) })] }), _jsx(Box, { className: "login-forgot-password-container", children: _jsx(AppButton, { children: "Forgot Password?", onClick: () => navigate(ROUTES.forgotPassword.path), className: "login-forgot-password-link" }) })] }), _jsx(Box, { className: "login-signin-button-container", children: _jsxs(AppButton, { type: "submit", fullWidth: true, className: "login-signin-button", variant: "contained", color: "primary", disabled: formik.isSubmitting, children: ["   ", formik.isSubmitting ? _jsx(CustomLoader, { color: "inherit" }) : 'Sign In'] }) })] })] }) }) }), _jsx("div", { className: "bird-container bird-container--one", children: _jsx("div", { className: "bird bird--one", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--two", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--three", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--four", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsxs(Dialog, { open: Boolean(selectionData), onClose: () => setSelectionData(null), PaperProps: {
                    sx: {
                        borderRadius: '8px',
                        padding: '32px 24px',
                        background: '#1c1c1c',
                        color: '#fff',
                        minWidth: '420px',
                        maxWidth: '450px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    }
                }, children: [_jsxs(Box, { sx: { textAlign: 'center', mb: 4 }, children: [_jsx(Typography, { sx: { color: '#ff0080', fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', mb: 1.5 }, children: "MULTIPLE ACCOUNTS DETECTED" }), _jsx(Typography, { variant: "h5", sx: { fontWeight: 700, mb: 1, fontSize: '24px' }, children: "How would you like to sign in?" }), _jsxs(Typography, { sx: { color: '#888', fontSize: '14px', fontWeight: 500 }, children: [_jsx("span", { style: { color: '#aaa' }, children: formik.values.email }), " is linked to two accounts"] })] }), _jsxs(Stack, { spacing: 2, children: [_jsxs(Box, { onClick: () => handleSelectLogin('user'), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: '20px 24px',
                                    borderRadius: '16px',
                                    border: '1px solid #ff0080',
                                    background: 'rgba(255, 0, 128, 0.04)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    // '&:hover': {
                                    //     background: 'rgba(255, 0, 128, 0.08)',
                                    //     transform: 'translateY(-2px)'
                                    // }
                                }, children: [_jsx(Box, { sx: {
                                            width: 44, height: 44, borderRadius: '8px', background: 'rgba(255, 0, 128, 0.1)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff0080'
                                        }, children: _jsx(PersonIcon, { sx: { fontSize: '24px' } }) }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: '16px', mb: 0.2 }, children: "User / Admin" }), _jsx(Typography, { sx: { color: '#888', fontSize: '12px', fontWeight: 500 }, children: "Access internal tools and settings" })] }), _jsx(ArrowForwardIosIcon, { sx: { fontSize: '12px', color: '#444' } })] }), _jsxs(Box, { onClick: () => handleSelectLogin('client'), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: '20px 24px',
                                    borderRadius: '16px',
                                    border: '1px solid #333',
                                    background: 'rgba(255, 255, 255, 0.02)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderColor: '#444',
                                        // transform: 'translateY(-2px)'
                                    }
                                }, children: [_jsx(Box, { sx: {
                                            width: 44, height: 44, borderRadius: '8px', background: 'rgba(255, 255, 255, 0.05)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888'
                                        }, children: _jsx(WorkOutlineIcon, { sx: { fontSize: '20px' } }) }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: '16px', mb: 0.2 }, children: "Client" }), _jsx(Typography, { sx: { color: '#888', fontSize: '12px', fontWeight: 500 }, children: "View reports and project updates" })] }), _jsx(ArrowForwardIosIcon, { sx: { fontSize: '12px', color: '#444' } })] })] }), _jsx(Box, { sx: { mt: 5, textAlign: 'center' }, children: _jsx(Typography, { onClick: () => setSelectionData(null), sx: {
                                color: '#666',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 600,
                                transition: 'color 0.2s',
                                '&:hover': { color: '#888' }
                            }, children: "Cancel" }) })] })] }));
}
